import { writable } from 'svelte/store';
import type { Writable } from 'svelte/store';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';

import { FilterFormLabels, FilterFormValues, FilterType } from './types';
import { OptionList } from '$saga/types/search.types';
import { defaultFormLabels, defaultFormValues } from './constants';

export const isSecondaryFiltersOpen = writable(false);

export type SearchStoreState = {
    formLabels: FilterFormLabels;
    formValues: FilterFormValues;
    selectedFilterType: FilterType;
    defaultDurationMin: number | undefined;
    defaultDurationMax: number | undefined;
    defaultBudgetMin: number | undefined;
    defaultBudgetMax: number | undefined;
    defaultLevels: OptionList | undefined;
    defaultTags: OptionList | undefined;
    defaultTypes: OptionList | undefined;
    defaultDestinationOptions: OptionList | undefined;
    defaultSportOptions: OptionList | undefined;
    isHomePage: boolean;
    isSubmitting: boolean;
    isDirty: boolean;
};

export const filtersContext: Writable<SearchStoreState> = writable({
    formLabels: defaultFormLabels,
    formValues: defaultFormValues,
    selectedFilterType: FilterType.SELECTION,
    defaultDurationMin: undefined,
    defaultDurationMax: undefined,
    defaultBudgetMin: undefined,
    defaultBudgetMax: undefined,
    defaultLevels: undefined,
    defaultTags: undefined,
    defaultTypes: undefined,
    defaultDestinationOptions: undefined,
    defaultSportOptions: undefined,
    isHomePage: false,
    isSubmitting: false,
    isDirty: false,
});

export const filteredToursCount = writable<number | null>(null);

export async function updateFilteredToursCount(formValues: FilterFormValues) {
    try {
        // build URL for GET request
        const queryParams = new URLSearchParams();
        Object.entries(formValues).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    // pop last 's' character from key if it ends with 's' because bad form naming in backend
                    if (key.endsWith('s')) {
                        key = key.slice(0, -1);
                    }
                    value.forEach((item) =>
                        queryParams.append(key + '[]', item),
                    );
                } else {
                    // Format DateValue objects and Date objects to DD/MM/YYYY to match backend expectations
                    let formattedValue = value?.toString() ?? '';
                    if (
                        value &&
                        typeof value === 'object' &&
                        'calendar' in value &&
                        'era' in value &&
                        'year' in value
                    ) {
                        // This is a DateValue object from @internationalized/date
                        formattedValue = dayjs(
                            `${value.year}-${value.month
                                .toString()
                                .padStart(2, '0')}-${value.day
                                .toString()
                                .padStart(2, '0')}`,
                        ).format('DD/MM/YYYY');
                    } else if (
                        value &&
                        typeof value === 'object' &&
                        'getTime' in value
                    ) {
                        // This is a regular Date object (e.g., from month selection)
                        formattedValue = dayjs(value as Date).format(
                            'DD/MM/YYYY',
                        );
                    }
                    queryParams.append(key, formattedValue);
                }
            }
        });
        const url = `/voyages/count?${queryParams.toString()}`;
        const response = await fetch(url, {
            headers: {
                'X-Count-Results': 'true',
            },
            method: 'GET',
        });

        if (response.ok) {
            const data = await response.json();
            filteredToursCount.set(data.count);
        } else {
            filteredToursCount.set(null);
        }
    } catch {
        filteredToursCount.set(null);
    }
}

let prevFormValues: FilterFormValues | null = null;
let isFirstRun = true;

const debouncedUpdateFilteredToursCount = debounce((formValues) => {
    // Only call if values actually changed
    if (JSON.stringify(formValues) !== JSON.stringify(prevFormValues)) {
        prevFormValues = JSON.parse(JSON.stringify(formValues));
        updateFilteredToursCount(formValues);
    }
}, 300);

// Subscribe to formValues changes and update count
filtersContext.subscribe(($ctx) => {
    if (isFirstRun) {
        isFirstRun = false;
        prevFormValues = JSON.parse(JSON.stringify($ctx.formValues));
        return;
    }

    // Check if form values have changed from initial values
    const hasChanged =
        JSON.stringify($ctx.formValues) !== JSON.stringify(prevFormValues);

    // Update isDirty if it has changed
    if (hasChanged !== $ctx.isDirty) {
        filtersContext.update((ctx) => ({
            ...ctx,
            isDirty: hasChanged,
        }));
    }

    debouncedUpdateFilteredToursCount($ctx.formValues);
});

// create a derived store to determine if any filters are active
export const areSecondaryFiltersActive = writable(false);
export const areMainFiltersActive = writable(false);

filtersContext.subscribe(($ctx) => {
    const values = $ctx.formValues;

    const isMainActive =
        values.theme.length > 0 ||
        values.destination.length > 0 ||
        values.nbPax !== defaultFormValues.nbPax ||
        values.startDate !== defaultFormValues.startDate ||
        values.endDate !== defaultFormValues.endDate;

    const isSecondaryActive =
        values.levels.length > 0 ||
        values.types.length > 0 ||
        values.budgetMin !== defaultFormValues.budgetMin ||
        values.budgetMax !== defaultFormValues.budgetMax ||
        values.durationMin !== defaultFormValues.durationMin ||
        values.durationMax !== defaultFormValues.durationMax ||
        values.tags.length > 0;

    areMainFiltersActive.set(isMainActive);
    areSecondaryFiltersActive.set(isSecondaryActive);
});
